import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Box, Title, Text, Paper, Stack, UnstyledButton, Loader, Center, Select, Image, Grid, Badge, Group, Divider } from '@mantine/core';
import { FormattedMessage, useIntl } from "react-intl";
import { BASE_URL } from '../Redux/apiConfig';

const translateHTML = async (html, targetLang) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const textNodes = [];
    const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null, false);
    let node;
    while (node = walker.nextNode()) {
      if (node.nodeValue.trim()) {
        textNodes.push(node);
      }
    }
    
    const textsToTranslate = textNodes.map(n => n.nodeValue.trim());
    
    const response = await fetch(`${BASE_URL}/api/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        texts: textsToTranslate,
        targetLang: targetLang,
        autoDetect: true
      })
    });
    
    const { translations, detectedLang } = await response.json();
    
    if (detectedLang === targetLang) {
      return html;
    }
    
    textNodes.forEach((node, i) => {
      node.nodeValue = translations[i];
    });
    
    return doc.body.innerHTML;
  } catch (error) {
    console.error('Translation error:', error);
    return html;
  }
};

// Color theme mapping
const colorThemes = {
  brown: { gradient: 'linear-gradient(135deg, #6B4423 0%, #3d2817 100%)', primary: '#6B4423', dark: '#3d2817' },
  green: { gradient: 'linear-gradient(135deg, #2d5016 0%, #1a3d0f 100%)', primary: '#2d5016', dark: '#1a3d0f' },
  blue: { gradient: 'linear-gradient(135deg, #1e3a8a 0%, #0c1d4d 100%)', primary: '#1e3a8a', dark: '#0c1d4d' },
  orange: { gradient: 'linear-gradient(135deg, #c2410c 0%, #7c2d12 100%)', primary: '#c2410c', dark: '#7c2d12' },
  purple: { gradient: 'linear-gradient(135deg, #6b21a8 0%, #4c1d95 100%)', primary: '#6b21a8', dark: '#4c1d95' },
  teal: { gradient: 'linear-gradient(135deg, #0f766e 0%, #134e4a 100%)', primary: '#0f766e', dark: '#134e4a' },
  red: { gradient: 'linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%)', primary: '#991b1b', dark: '#7f1d1d' },
  slate: { gradient: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)', primary: '#475569', dark: '#1e293b' },
};

// Helper function to convert old infoGrid object format to new array format
const normalizeInfoGrid = (infoGridData) => {
  // If it's already an array, return as-is
  if (Array.isArray(infoGridData)) {
    return infoGridData;
  }
  
  // If it's an object (old format), convert to array format
  if (infoGridData && typeof infoGridData === 'object') {
    const mapping = {
      producer: 'Producer',
      locationInfo: 'Location',
      extension: 'Extension',
      mainCrop: 'Main Crop',
      experience: 'Experience',
      practices: 'Practices',
      certifications: 'Certifications',
      altitude: 'Altitude'
    };
    
    return Object.entries(mapping).map(([key, label]) => ({
      label,
      value: infoGridData[key] || ''
    })).filter(item => item.value); // Remove empty values
  }
  
  // Default empty array
  return [];
};

// Helper function to normalize profile data (convert old formats to new)
const normalizeProfileData = (data) => {
  if (!data) return null;
  
  return {
    ...data,
    infoGrid: normalizeInfoGrid(data.infoGrid),
    // Ensure other arrays exist
    gallery: Array.isArray(data.gallery) ? data.gallery : [],
    documents: Array.isArray(data.documents) ? data.documents : [],
    productLots: Array.isArray(data.productLots) ? data.productLots : [],
    customLinks: Array.isArray(data.customLinks) ? data.customLinks : [],
  };
};

export default function ProfilePresentation({ onTemplateStatusChange, accountIdOverride = null }) {
  const { accountId: accountIdFromRoute } = useParams();
  const accountId = accountIdOverride || accountIdFromRoute;
  const [profileData, setProfileData] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [customHtml, setCustomHtml] = useState(null);
  const [hasCustomTemplate, setHasCustomTemplate] = useState(false);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [notPublished, setNotPublished] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const [selectedLang, setSelectedLang] = useState(() => {
    return localStorage.getItem('profileLang') || navigator.language.split('-')[0];
  });
  const [originalHtml, setOriginalHtml] = useState(null);
  const [translatedProfileData, setTranslatedProfileData] = useState(null);
  const [documentsLabel, setDocumentsLabel] = useState('Documents');

  const logView = (viewType) => {
    fetch(`${BASE_URL}/api/logProfileView`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accountId,
        viewType, // 'custom' or 'default'
        language: selectedLang
      })
    }).catch(() => {}); // Silent fail
  };

  // Check for background image in multiple formats
  useEffect(() => {
    const checkBackgroundImage = async () => {
      if (!accountId) return;
      
      const formats = ['jpg', 'jpeg', 'png', 'webp'];
      const baseUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/background`;
      
      for (const format of formats) {
        const imageUrl = `${baseUrl}.${format}`;
        try {
          const response = await fetch(imageUrl, { method: 'HEAD' });
          if (response.ok) {
            console.log(`✅ Found background image: ${imageUrl}`);
            setBackgroundImageUrl(imageUrl);
            return;
          }
        } catch (error) {
          // Continue to next format
        }
      }
      
      console.log('ℹ️ No background image found, using default');
      setBackgroundImageUrl(null);
    };
    
    checkBackgroundImage();
  }, [accountId]);

  useEffect(() => {
    const translateProfileData = async () => {
      if (!profileData || !profileData.title) return;
      
      localStorage.setItem('profileLang', selectedLang);
      
      if (selectedLang === 'en') {
        setTranslatedProfileData(profileData);
        setDocumentsLabel('Documents');
        return;
      }
      
      try {
        // Translate title, subtitle, and Documents label
        const response = await fetch(`${BASE_URL}/api/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            texts: [profileData.title, profileData.subtitle, 'Documents'],
            targetLang: selectedLang,
            autoDetect: true
          })
        });
        
        const { translations } = await response.json();
        
        // Set Documents label translation
        setDocumentsLabel(translations[2] || 'Documents');
        
        // Translate document names
        const docNames = profileData.documents?.map(d => d.name) || [];
        if (docNames.length > 0) {
          const docResponse = await fetch(`${BASE_URL}/api/translate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              texts: docNames,
              targetLang: selectedLang,
              autoDetect: true
            })
          });
          
          const { translations: docTranslations } = await docResponse.json();
          
          setTranslatedProfileData({
            ...profileData,
            title: translations[0] || profileData.title,
            subtitle: translations[1] || profileData.subtitle,
            documents: profileData.documents?.map((doc, i) => ({
              ...doc,
              name: docTranslations[i] || doc.name
            }))
          });
        } else {
          setTranslatedProfileData({
            ...profileData,
            title: translations[0] || profileData.title,
            subtitle: translations[1] || profileData.subtitle,
          });
        }
      } catch (error) {
        console.error('Translation error:', error);
        setTranslatedProfileData(profileData);
        setDocumentsLabel('Documents');
      }
    };
    
    translateProfileData();
  }, [selectedLang, profileData]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!accountId) {
        setIsLoading(false);
        return;
      }

      try {
        // FIRST: Check subscription status via backend API (applies to all profile types)
        console.log('🔒 Checking subscription status...');
        const subscriptionCheckResponse = await fetch(`${BASE_URL}/api/profile/${accountId}`);
        console.log('📡 Subscription check response status:', subscriptionCheckResponse.status);
        
        if (subscriptionCheckResponse.status === 403) {
          // Subscription has expired - block all profile access
          setSubscriptionExpired(true);
          console.error('❌ Subscription expired (403 Forbidden) - blocking profile access');
          setIsLoading(false);
          return;
        }

        if (subscriptionCheckResponse.status === 404) {
          // Could be unpublished or genuinely missing
          try {
            const errData = await subscriptionCheckResponse.json();
            if (errData.error === 'Profile not published') {
              setNotPublished(true);
              setIsLoading(false);
              return;
            }
          } catch (e) {}
          setIsLoading(false);
          return;
        }
        
        // Subscription is valid, proceed to load profile content
        console.log('✅ Subscription valid, loading profile...');

        // Read profile data once (needed for displayMode and as form-profile fallback)
        const cachedProfileData = subscriptionCheckResponse.ok ? await subscriptionCheckResponse.json() : null;
        const displayMode = cachedProfileData?.profileDisplayMode; // 'form' | 'custom' | undefined

        const loadFormProfile = async () => {
          onTemplateStatusChange?.(false);
          if (cachedProfileData) {
            setProfileData(normalizeProfileData(cachedProfileData));
            logView('default');
          } else {
            console.error('❌ Failed to fetch profile data, status:', subscriptionCheckResponse.status);
          }
          const qrResponse = await fetch(`${BASE_URL}/api/getProfileQRCode/${accountId}`);
          if (qrResponse.ok) {
            const qrData = await qrResponse.json();
            setQrCodeUrl(qrData.qrCodeUrl);
          }
        };

        if (displayMode === 'form') {
          // User explicitly chose the form-based profile — skip custom HTML entirely
          console.log('📋 profileDisplayMode is "form" — rendering form profile');
          await loadFormProfile();
        } else {
          // Try custom HTML (default / 'custom' mode)
          const htmlResponse = await fetch(`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/ProfilePresentation.html`);

          if (htmlResponse.ok) {
            const htmlContent = await htmlResponse.text();
            const trimmedContent = htmlContent.trim();
            const hasValidContent = trimmedContent.length > 50 &&
                                   (trimmedContent.includes('<html') || trimmedContent.includes('<!DOCTYPE'));

            if (hasValidContent) {
              console.log('✅ Custom HTML template loaded from blob storage');
              setOriginalHtml(htmlContent);
              setHasCustomTemplate(true);
              onTemplateStatusChange?.(true);
              logView('custom');
            } else {
              console.log('⚠️ ProfilePresentation.html exists but is empty/invalid, falling back to default profile');
              await loadFormProfile();
            }
          } else {
            console.log('ℹ️ No custom template found, fetching default profile data...');
            await loadFormProfile();
          }
        }
      } catch (error) {
        console.error('❌ Error fetching profile:', error);
        onTemplateStatusChange?.(false);
      }
      setIsLoading(false);
    };

    fetchProfile();
  }, [accountId, onTemplateStatusChange]);

  useEffect(() => {
    const translateContent = async () => {
      if (!originalHtml) return;
      
      localStorage.setItem('profileLang', selectedLang);
      
      if (selectedLang === 'en') {
        setCustomHtml(originalHtml);
        return;
      }
      
      const translated = await translateHTML(originalHtml, selectedLang);
      setCustomHtml(translated);
    };
    
    translateContent();
  }, [selectedLang, originalHtml]);

  if (isLoading) return (
    <Center style={{ minHeight: '100vh' }}>
      <Loader />
      <Text style={{ marginLeft: '10px' }}><FormattedMessage id="profile.loading" /></Text>
    </Center>
  );

  // Profile not published - show neutral not-available message
  if (notPublished) {
    return (
      <Box style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <Paper p="4rem" radius="xl" shadow="xl" style={{ maxWidth: '500px', textAlign: 'center', background: 'white' }}>
          <Box style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>🔒</Box>
          <Title order={2} mb="md" style={{ color: '#475569' }}>
            <FormattedMessage id="profile.notpublished.title" defaultMessage="Profile Not Available" />
          </Title>
          <Text size="lg" style={{ color: '#666', lineHeight: 1.6 }}>
            <FormattedMessage id="profile.notpublished.message" defaultMessage="This profile is not currently published." />
          </Text>
        </Paper>
      </Box>
    );
  }

  // Subscription expired - show nice error message
  if (subscriptionExpired) {
    return (
      <Box style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <Paper p="4rem" radius="xl" shadow="xl" style={{ maxWidth: '600px', textAlign: 'center', background: 'white' }}>
          {/* Logo/Icon */}
          <Box style={{ fontSize: '5rem', marginBottom: '2rem' }}>
            🔐
          </Box>
          
          <Title order={1} mb="lg" style={{ fontSize: '2.5rem', color: '#991b1b' }}>
            <FormattedMessage id="profile.subscriptionExpired.title" defaultMessage="Subscription Expired" />
          </Title>
          
          <Text size="xl" mb="2rem" style={{ color: '#666', lineHeight: 1.6 }}>
            <FormattedMessage 
              id="profile.subscriptionExpired.message" 
              defaultMessage="Your subscription has expired. To regain access to your profile and continue using our services, please contact our support team." 
            />
          </Text>
          
          <Box style={{ background: '#fef3f2', border: '2px solid #fecaca', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
            <Text size="lg" weight={600} mb="xs" style={{ color: '#991b1b' }}>
              ✉️ <FormattedMessage id="profile.subscriptionExpired.contactSupport" defaultMessage="Contact Support" />
            </Text>
            <Text size="md" style={{ color: '#666' }}>
              support@vertocommodities.io
            </Text>
          </Box>
          
          <Stack spacing="md">
            <UnstyledButton
              onClick={() => window.location.href = 'mailto:support@vertocommodities.io'}
              style={{
                background: 'linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: 600,
                boxShadow: '0 4px 15px rgba(153, 27, 27, 0.4)',
                transition: 'transform 0.2s ease',
                border: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              📧 <FormattedMessage id="profile.subscriptionExpired.emailSupport" defaultMessage="Email Support" />
            </UnstyledButton>
            
            <Text size="sm" style={{ color: '#999' }}>
              <FormattedMessage id="profile.subscriptionExpired.responseTime" defaultMessage="Our support team will respond within 24 hours" />
            </Text>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (!profileData && !customHtml) {
    console.log('⚠️ No profile data and no custom HTML - showing not found');
    console.log('   - profileData:', profileData);
    console.log('   - customHtml:', customHtml);
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Text><FormattedMessage id="profile.notfound" /></Text>
      </Center>
    );
  }

  // Custom HTML template view
  if (customHtml) {
    console.log('🎨 Rendering custom HTML template in iframe');
    return (
      <iframe
        src={`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/ProfilePresentation.html`}
        style={{
          width: '100%',
          height: '100vh',
          border: 'none'
        }}
        title="Profile Presentation"
      />
    );
  }

  // Default profile view
  const data = translatedProfileData || profileData;
  console.log('🖼️ Rendering default profile view');
  console.log('   - Using data:', data);
  console.log('   - translatedProfileData:', translatedProfileData);
  console.log('   - profileData:', profileData);
  const headerTheme = colorThemes[data?.headerColor] || colorThemes.brown;
  
  return (
    <>
      {/* LANGUAGE SELECTOR - FIXED AT TOP RIGHT */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 99999,
        width: 'auto',
        height: 'auto',
        display: 'inline-block',
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(0, 0, 0, 0.8)',
          borderRadius: '8px',
          padding: '8px 12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          width: 'auto',
          height: 'auto',
        }}>
          <span style={{ fontSize: '20px' }}>🌍</span>
          <select 
            value={selectedLang}
            onChange={(e) => setSelectedLang(e.target.value)}
            style={{
              padding: '6px 10px',
              border: '2px solid white',
              borderRadius: '6px',
              background: 'white',
              color: '#333',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '140px',
              outline: 'none',
              height: 'auto',
            }}
          >
            <option value="es">Español</option>
            <option value="en">English</option>
            <option value="fr">Français</option>
            <option value="de">Deutsch</option>
            <option value="it">Italiano</option>
            <option value="pt">Português</option>
            <option value="nl">Nederlands</option>
            <option value="ja">日本語</option>
            <option value="ko">한국어</option>
            <option value="zh">中文</option>
            <option value="ar">العربية</option>
            <option value="ru">Русский</option>
          </select>
        </div>
      </div>

      <Box style={{
        minHeight: '100vh',
        backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        // Use scroll instead of fixed for better mobile compatibility
        // Fixed backgrounds don't work properly on iOS Safari and many mobile browsers
        backgroundAttachment: 'scroll',
      }}>
        {/* Header */}
        <Box style={{
          background: headerTheme.gradient,
          color: 'white',
          padding: '4.5rem 2rem 3rem 2rem',
          textAlign: 'center',
          position: 'relative',
        }}>
          <Title order={1} style={{ fontSize: '2.5rem', marginBottom: '0.5rem', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            {String(data?.title || 'Business Profile')}
          </Title>
          <Text size="xl" style={{ fontSize: '1.2rem', opacity: 0.9 }}>
            {String(data?.subtitle || 'Premium Products')}
          </Text>
        </Box>

        <Container size="xl" style={{ padding: '2rem', background: 'rgba(255, 255, 255, 0.95)' }}>
          {data?.heroImage && (
            <Box style={{ 
              width: '100%', 
              maxWidth: '900px',
              marginLeft: 'auto',
              marginRight: 'auto',
              marginBottom: '2rem',
              overflow: 'hidden',
              borderRadius: '8px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}>
              <img
                src={data.heroImage}
                alt="Hero"
                style={{ 
                  width: '100%',
                  height: '300px',
                  objectFit: 'cover', 
                  display: 'block'
                }}
              />
            </Box>
          )}

          {/* About Section */}
          {data?.description && (
            <Paper p="xl" mb="xl" radius="md" shadow="sm" style={{ background: 'white' }}>
              <Title order={2} mb="md" style={{ color: headerTheme.primary, borderBottom: `3px solid ${headerTheme.primary}`, paddingBottom: '0.5rem' }}>
                <FormattedMessage id="profile.about" defaultMessage="About" />
              </Title>
              <Box style={{
                background: 'linear-gradient(135deg, #f0e6d6 0%, #ffffff 100%)',
                padding: '2rem',
                borderRadius: '10px',
                borderLeft: `5px solid ${headerTheme.primary}`,
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: '#555',
              }}>
                <Text style={{ whiteSpace: 'pre-wrap' }}>{String(data.description)}</Text>
              </Box>

              {/* Info Grid */}
              {data?.infoGrid && Array.isArray(data.infoGrid) && (
                <Grid mt="xl" gutter="lg">
                  {data.infoGrid.map((item, index) => (
                    <Grid.Col key={index} span={6} md={3}>
                      <Box style={{
                        background: '#f9f9f9',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${headerTheme.primary}`,
                      }}>
                        <Text size="xs" weight={600} style={{ color: headerTheme.primary, textTransform: 'uppercase', marginBottom: '0.5rem', letterSpacing: '1px' }}>
                          {String(item?.label || '')}
                        </Text>
                        <Text size="md" style={{ color: '#555' }}>
                          {String(item?.value || '')}
                        </Text>
                      </Box>
                    </Grid.Col>
                  ))}
                </Grid>
              )}
            </Paper>
          )}

          {/* Location Section */}
          {data?.location && (
            <Paper p="xl" mb="xl" radius="md" shadow="sm" style={{ background: 'white' }}>
              <Title order={2} mb="md" style={{ color: headerTheme.primary, borderBottom: `3px solid ${headerTheme.primary}`, paddingBottom: '0.5rem' }}>
                📍 <FormattedMessage id="profile.location" defaultMessage="Location" />
              </Title>

              {/* Location Badge */}
              <Box style={{
                background: headerTheme.gradient,
                color: 'white',
                padding: '1.5rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                textAlign: 'center',
                boxShadow: `0 5px 15px ${headerTheme.primary}40`,
              }}>
                <Text size="xl" weight={600} mb="xs">
                  🌿 {String(data.location.address || '')}
                </Text>
                <Text size="md" style={{ opacity: 0.95, whiteSpace: 'pre-wrap' }}>
                  {String(data.location.description || '')}
                </Text>
              </Box>

              {/* Map */}
              {data.location.mapUrl && (
                <Box style={{
                  width: '100%',
                  height: '450px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  marginBottom: '2rem',
                }}>
                  <iframe
                    src={data.location.mapUrl}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    allowFullScreen
                    loading="lazy"
                    title="Location Map"
                  />
                </Box>
              )}

              {/* Coordinates */}
              {(data.location.coordinates || data.location.coordinatesDMS) && (
                <Grid gutter="lg" mb="lg">
                  {data.location.coordinates && (
                    <Grid.Col span={6}>
                      <Box style={{
                        background: 'linear-gradient(135deg, #f0e6d6 0%, #ffffff 100%)',
                        padding: '1.5rem',
                        borderRadius: '10px',
                        borderLeft: `4px solid ${headerTheme.primary}`,
                        textAlign: 'center',
                      }}>
                        <Text size="sm" weight={600} style={{ color: headerTheme.primary, marginBottom: '0.8rem' }}>
                          <FormattedMessage id="profile.coordinates.decimal" defaultMessage="Decimal Coordinates" />
                        </Text>
                        <Text size="md" weight={500} style={{ fontFamily: 'monospace', color: '#555' }}>
                          {String(data.location.coordinates)}
                        </Text>
                      </Box>
                    </Grid.Col>
                  )}
                  {data.location.coordinatesDMS && (
                    <Grid.Col span={6}>
                      <Box style={{
                        background: 'linear-gradient(135deg, #f0e6d6 0%, #ffffff 100%)',
                        padding: '1.5rem',
                        borderRadius: '10px',
                        borderLeft: `4px solid ${headerTheme.primary}`,
                        textAlign: 'center',
                      }}>
                        <Text size="sm" weight={600} style={{ color: headerTheme.primary, marginBottom: '0.8rem' }}>
                          <FormattedMessage id="profile.coordinates.dms" defaultMessage="DMS Coordinates" />
                        </Text>
                        <Text size="md" weight={500} style={{ fontFamily: 'monospace', color: '#555' }}>
                          {String(data.location.coordinatesDMS)}
                        </Text>
                      </Box>
                    </Grid.Col>
                  )}
                </Grid>
              )}

              {/* Open in Maps Button */}
              {data.location.coordinates && (
                <Center>
                  <UnstyledButton
                    onClick={() => window.open(`https://www.google.com/maps?q=${data.location.coordinates}`, '_blank')}
                    style={{
                      background: headerTheme.primary,
                      color: 'white',
                      padding: '1rem 2rem',
                      borderRadius: '8px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      boxShadow: `0 4px 15px ${headerTheme.primary}40`,
                    }}
                  >
                    🗺️ <FormattedMessage id="profile.openInMaps" defaultMessage="Open in Google Maps" />
                  </UnstyledButton>
                </Center>
              )}
            </Paper>
          )}

          {/* Image Gallery */}
          {data?.gallery && Array.isArray(data.gallery) && data.gallery.length > 0 && (
            <Grid gutter="lg" mb="xl">
              {data.gallery.map((imageUrl, index) => (
                <Grid.Col key={index} span={12} md={6} lg={4}>
                  <Box 
                    style={{
                      width: '100%',
                      height: '250px',
                      overflow: 'hidden',
                      borderRadius: '8px',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
                      transition: 'transform 0.3s ease',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <img
                      src={imageUrl}
                      alt={`Gallery ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                  </Box>
                </Grid.Col>
              ))}
            </Grid>
          )}

          {/* Product Lots */}
          {data?.productLots && Array.isArray(data.productLots) && data.productLots.length > 0 && (
            <Paper p="xl" mb="xl" radius="md" shadow="sm" style={{ background: 'white' }}>
              <Title order={2} mb="md" style={{ color: headerTheme.primary, borderBottom: `3px solid ${headerTheme.primary}`, paddingBottom: '0.5rem' }}>
                <FormattedMessage id="profile.productLots" defaultMessage="Our Product Lots" />
              </Title>
              <Text mb="xl" size="lg" style={{ color: '#666' }}>
                <FormattedMessage id="profile.productLots.description" defaultMessage="Explore our certified product lots with full traceability and quality certification." />
              </Text>

              <Grid gutter="lg">
                {data.productLots.map((lot, index) => (
                  <Grid.Col key={index} span={12} md={6} lg={4}>
                    <Box style={{
                      background: 'linear-gradient(135deg, #f9f9f9 0%, #ffffff 100%)',
                      padding: '1.5rem',
                      borderRadius: '10px',
                      border: '2px solid #e0e0e0',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                        e.currentTarget.style.borderColor = headerTheme.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = '#e0e0e0';
                      }}
                    >
                      <Box style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '4px',
                        background: headerTheme.gradient,
                      }} />
                      <Title order={3} mb="md" style={{ color: headerTheme.primary, fontSize: '1.3rem' }}>
                        {String(lot?.code || '')}
                      </Title>
                      <Text mb="md">{String(lot?.name || '')}</Text>
                      <UnstyledButton
                        onClick={() => window.open(lot.certificateUrl, '_blank')}
                        style={{
                          display: 'inline-block',
                          background: headerTheme.primary,
                          color: 'white',
                          padding: '0.7rem 1.5rem',
                          borderRadius: '5px',
                          fontWeight: 600,
                          transition: 'background 0.3s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = headerTheme.dark}
                        onMouseLeave={(e) => e.currentTarget.style.background = headerTheme.primary}
                      >
                        <FormattedMessage id="profile.viewCertificate" defaultMessage="View Certificate" /> →
                      </UnstyledButton>
                    </Box>
                  </Grid.Col>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Custom / Additional Links */}
          {data?.customLinks && data.customLinks.filter(l => l.url).length > 0 && (
            <Box style={{
              background: headerTheme.gradient,
              color: 'white',
              padding: '2rem',
              borderRadius: '10px',
              marginTop: '2rem',
            }}>
              <Text size="md" weight={600} mb="md">
                <FormattedMessage id="profile.section.customlinks" defaultMessage="Custom Links" />
              </Text>
              <Group spacing="md">
                {data.customLinks.filter(l => l.url).map((link, idx) => (
                  <UnstyledButton
                    key={idx}
                    onClick={() => window.open(link.url, '_blank')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.6rem 1.2rem',
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      transition: 'transform 0.2s ease',
                      border: '2px solid rgba(255,255,255,0.3)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    🔗 {link.title || link.url}
                  </UnstyledButton>
                ))}
              </Group>
            </Box>
          )}

          {/* Contact Info */}
          {data?.contact && (
            <Box style={{
              background: headerTheme.gradient,
              color: 'white',
              padding: '2rem',
              borderRadius: '10px',
              marginTop: '2rem',
            }}>
              <Title order={3} mb="lg" style={{ fontSize: '1.5rem' }}>
                <FormattedMessage id="profile.contact" defaultMessage="Contact Information" />
              </Title>
              {data.contact.name && (
                <Text mb="xs" size="lg">
                  <strong><FormattedMessage id="profile.contact.name" defaultMessage="Contact" />:</strong> {String(data.contact.name)}
                </Text>
              )}
              {data.contact.phone && (
                <Text mb="xs" size="lg">
                  <strong><FormattedMessage id="profile.contact.phone" defaultMessage="Phone" />:</strong> {String(data.contact.phone)}
                </Text>
              )}
              {data.contact.email && (
                <Text mb="xs" size="lg">
                  <strong>Email:</strong> {String(data.contact.email)}
                </Text>
              )}
              {data.contact.address && (
                <Text mb="xs" size="lg">
                  <strong><FormattedMessage id="profile.contact.address" defaultMessage="Address" />:</strong> {String(data.contact.address)}
                </Text>
              )}
            </Box>
          )}

          {/* Social Media Links */}
          {data?.socialLinks && (data.socialLinks.instagram || data.socialLinks.twitter || data.socialLinks.website) && (
            <Box style={{
              background: headerTheme.gradient,
              color: 'white',
              padding: '2rem',
              borderRadius: '10px',
              marginTop: '2rem',
            }}>
              <Text size="md" weight={600} mb="md">
                <FormattedMessage id="profile.social.title" defaultMessage="Connect With Us" />
              </Text>
              <Group spacing="md">
                {data.socialLinks.instagram && (
                  <UnstyledButton
                    onClick={() => window.open(data.socialLinks.instagram, '_blank')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.6rem 1.2rem',
                      background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      transition: 'transform 0.2s ease',
                      border: '2px solid rgba(255,255,255,0.2)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    📷 Instagram
                  </UnstyledButton>
                )}
                {data.socialLinks.twitter && (
                  <UnstyledButton
                    onClick={() => window.open(data.socialLinks.twitter, '_blank')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.6rem 1.2rem',
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      transition: 'transform 0.2s ease',
                      border: '2px solid rgba(255,255,255,0.3)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    𝕏 X
                  </UnstyledButton>
                )}
                {data.socialLinks.website && (
                  <UnstyledButton
                    onClick={() => window.open(data.socialLinks.website, '_blank')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.6rem 1.2rem',
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      transition: 'transform 0.2s ease',
                      border: '2px solid rgba(255,255,255,0.3)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    🌐 Website
                  </UnstyledButton>
                )}
              </Group>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
}